import {
  FormProvider,
  SimpleGrid,
  TextField,
  useDebounce,
  useForm,
  Flex,
  Spinner,
} from '@agrotoken-tech/ui';
import { useTranslation } from '@bcba/react-hooks';
import { useEffect } from 'react';
import { getSearchCreditor } from '../../../../services/crop-pledge';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LocaleKeys } from '@bcba/translations';
import { useCropPledgeCreditorContext } from './context/CropPledgeCreditorContext';

export const PledgeCreditorFiscalIdSearchForm = () => {
  const { t } = useTranslation();
  const params = useParams();
  const cropPledgeUuid = params['uuid'] || '';
  const { setSelectedCreditorData } = useCropPledgeCreditorContext();

  const formMethods = useForm();
  const { watch, setError, clearErrors } = formMethods;

  const debouncedCompanyFiscalId = useDebounce(watch('companyFiscalId'), 500);

  const {
    data: creditorSearchData,
    error,
    isLoading: isSearchingForCreditor,
  } = useQuery({
    queryKey: ['creditor-search', cropPledgeUuid, debouncedCompanyFiscalId],
    queryFn: () => getSearchCreditor(cropPledgeUuid, debouncedCompanyFiscalId),
    enabled: Boolean(cropPledgeUuid) && Boolean(debouncedCompanyFiscalId),
    retry: false,
  });

  useEffect(() => {
    if (error) {
      const errorResponse = error as any;
      const errorCode = errorResponse?.code;
      if (errorCode && t(`toast.error.${errorCode}.text` as LocaleKeys)) {
        setError('companyFiscalId', {
          message: t(`common.toast.error.${errorCode}.text` as LocaleKeys),
        });
      } else {
        setError('companyFiscalId', {
          message: t('common.genericError'),
        });
      }
      setSelectedCreditorData(undefined);
    } else {
      clearErrors('companyFiscalId');
    }
  }, [error, setError, clearErrors, t]);

  useEffect(() => {
    if (debouncedCompanyFiscalId && creditorSearchData) {
      setSelectedCreditorData(creditorSearchData);
    }
  }, [debouncedCompanyFiscalId, creditorSearchData, setSelectedCreditorData]);

  return (
    <SimpleGrid columns={2} mt="6" spacing={6} as="form" noValidate>
      <FormProvider {...formMethods}>
        <Flex>
          <TextField
            name="companyFiscalId"
            label={t('cropPledge.creditor.searchForm.fiscalId.label')}
            placeholder={t(
              'cropPledge.creditor.searchForm.fiscalId.placeholder',
            )}
            errorMessageFormatter={t}
            type="number"
            rhfOptions={{ required: true }}
          />
          {isSearchingForCreditor && (
            <Flex ml="12px" alignItems="end" mb={2}>
              <Spinner />
            </Flex>
          )}
        </Flex>
      </FormProvider>
    </SimpleGrid>
  );
};
